<template>
    <admin-island-shell
        title="Vendite private"
        subtitle="Party, listening e bundle"
        :count="totalCount"
        :theme-key="payload.themeKey || 'default'"
    >
        <template #actions>
            <button type="button" class="admin-vue-kit__cta" @click="openForm(emptySale())">Nuova vendita</button>
        </template>

        <admin-data-table
            ref="table"
            filters-only
            table-label="Vendite private"
            empty-message="Nessuna vendita presente."
            record-label-singular="vendita"
            record-label-plural="vendite"
            all-scope-label="Tutte le vendite attive"
            filtered-scope-label="Risultati filtrati"
            :definition="payload.rowDefinition"
            :listing-url="payload.routes.listing"
            :permissions="tablePermissions"
            :routes="tableRoutes"
            @restore="restoreSale"
            @force-delete="forceDeleting = $event"
            @loaded="onTableLoaded"
        >
            <template #cell-name="{ row }">
                <span :title="row.name" :class="{ 'admin-vue-kit__name--deleted': row.deleted }">{{ row.name }}</span>
            </template>
            <template #cell-type="{ row }">
                <sale-type-badge :label="row.type" />
            </template>
            <template #cell-start="{ row }">
                <span>{{ datePart(row.start) }}</span><span class="admin-vue-kit__time-part">{{ timePart(row.start) }}</span>
            </template>
            <template #cell-end="{ row }">
                <span>{{ datePart(row.end) }}</span><span class="admin-vue-kit__time-part">{{ timePart(row.end) }}</span>
            </template>
            <template #row-actions="{ row }">
                <button type="button" class="admin-vue-kit__row-cta admin-vue-kit__row-cta--primary" :aria-label="`Prodotto ${row.name}`" @click="openProducts(row)">Prodotto</button>
                <button type="button" class="admin-vue-kit__row-cta" :aria-label="`Modifica ${row.name}`" @click="openForm(row)">Modifica</button>
                <a
                    v-if="saleHasProduct(row)"
                    class="admin-vue-kit__row-cta"
                    :href="routeFor('preview', row.id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="`Anteprima ${row.name}`"
                    title="Apri landing in una nuova scheda"
                >Anteprima</a>
                <span
                    v-else
                    class="admin-vue-kit__row-cta"
                    role="button"
                    aria-disabled="true"
                    :aria-label="`Anteprima ${row.name} (aggiungi un prodotto)`"
                    title="Aggiungi almeno un prodotto per l’anteprima"
                >Anteprima</span>
                <button
                    type="button"
                    class="admin-vue-kit__row-cta"
                    :disabled="!saleHasProduct(row)"
                    :aria-label="saleHasProduct(row) ? `QR ${row.name}` : `QR ${row.name} (aggiungi un prodotto)`"
                    :title="saleHasProduct(row) ? 'Scarica QR' : 'Aggiungi almeno un prodotto per il QR'"
                    @click="openQr(row)"
                >QR</button>
                <a class="admin-vue-kit__row-cta" :href="routeFor('show', row.id)">Dettagli</a>
                <button type="button" class="admin-vue-kit__row-cta admin-vue-kit__row-cta--danger" :aria-label="`Elimina ${row.name}`" @click="deleting = row">Elimina</button>
            </template>
        </admin-data-table>

        <admin-dialog
            :open="Boolean(editing)"
            :title="editing?.id ? 'Modifica vendita' : 'Nuova vendita'"
            help="Dettagli della vendita privata"
            @update:open="(open) => { if (!open) editing = null; }"
        >
            <form v-if="editing" @submit.prevent="save">
                <label class="admin-vue-kit__field">Nome
                    <input v-model="editing.name" required placeholder="Nome della vendita" autofocus>
                </label>
                <label class="admin-vue-kit__field">Descrizione
                    <textarea v-model="editing.description" rows="3" placeholder="Note della vendita" />
                </label>
                <p class="admin-dialog__help">Slug: {{ editing.id ? editing.slug : (slugFromName(editing.name) || '—') }}</p>
                <label class="admin-vue-kit__field">Tipo
                    <select v-model="editing.type">
                        <option value="party">party</option>
                        <option value="listening">listening</option>
                        <option value="bundle">bundle</option>
                    </select>
                </label>
                <label class="admin-vue-kit__field">Inizio
                    <input v-model="editing.start" required type="datetime-local">
                </label>
                <label class="admin-vue-kit__field">Fine
                    <input v-model="editing.end" required type="datetime-local">
                </label>
                <div class="admin-dialog__actions">
                    <button type="button" class="admin-vue-kit__btn--outline" @click="editing = null">Annulla</button>
                    <button type="submit" class="admin-vue-kit__btn--primary" :disabled="saving">{{ editing.id ? 'Salva modifiche' : 'Crea vendita' }}</button>
                </div>
            </form>
        </admin-dialog>

        <admin-dialog
            :open="Boolean(productsSale)"
            :title="productsSale ? `Prodotto · ${productsSale.name}` : ''"
            help="Un solo prodotto per vendita. La quantità vuota non mette un tetto."
            wide
            @update:open="(open) => { if (!open) productsSale = null; }"
        >
            <form v-if="productsSale" @submit.prevent="saveProducts">
                <label class="admin-vue-kit__field">Cerca
                    <input
                        v-model="productQuery"
                        placeholder="EAN o titolo"
                        :disabled="productLines.length >= 1"
                        @input="scheduleProductSearch"
                    >
                </label>
                <ul v-if="productResults.length && productLines.length < 1" class="admin-vue-kit__product-results">
                    <li v-for="hit in productResults" :key="hit.ean">
                        <button type="button" class="admin-vue-kit__ghost-btn" style="width:100%;text-align:left;padding:0.5rem 0.75rem" @click="addProduct(hit)">{{ hit.text }}</button>
                    </li>
                </ul>
                <div v-if="productLines.length">
                    <div v-for="line in productLines" :key="line.ean" class="admin-vue-kit__product-line">
                        <div>
                            <strong>{{ line.text || line.ean }}</strong>
                            <div class="admin-vue-kit__cell--slug">{{ line.ean }}</div>
                        </div>
                        <label class="admin-vue-kit__field" style="margin-top:0">Qtà
                            <input v-model="line.available_quantity" type="number" min="0" placeholder="nessuna">
                        </label>
                        <button type="button" class="admin-vue-kit__ghost-btn admin-vue-kit__btn--danger" @click="removeProduct(line.ean)">Togli</button>
                    </div>
                </div>
                <p v-else class="admin-dialog__help">Nessun prodotto associato.</p>
                <div class="admin-dialog__actions">
                    <button type="button" class="admin-vue-kit__btn--outline" @click="productsSale = null">Annulla</button>
                    <button type="submit" class="admin-vue-kit__btn--primary" :disabled="saving">Salva prodotto</button>
                </div>
            </form>
        </admin-dialog>

        <admin-dialog
            :open="Boolean(deleting)"
            title="Eliminare la vendita?"
            :help="deleting ? `«${deleting.name}» esce dall’elenco. Resta recuperabile dallo storico.` : ''"
            @update:open="(open) => { if (!open) deleting = null; }"
        >
            <div class="admin-dialog__actions">
                <button type="button" class="admin-vue-kit__btn--outline" @click="deleting = null">Annulla</button>
                <button type="button" class="admin-vue-kit__btn--danger" @click="removeSale">Elimina vendita</button>
            </div>
        </admin-dialog>

        <admin-dialog
            :open="Boolean(forceDeleting)"
            title="Cancellare definitivamente?"
            :help="forceDeleting ? `«${forceDeleting.name}» verrà rimossa in modo permanente.` : ''"
            @update:open="(open) => { if (!open) forceDeleting = null; }"
        >
            <div class="admin-dialog__actions">
                <button type="button" class="admin-vue-kit__btn--outline" @click="forceDeleting = null">Annulla</button>
                <button type="button" class="admin-vue-kit__btn--danger" @click="forceDestroySale">Cancella definitivamente</button>
            </div>
        </admin-dialog>

        <admin-dialog
            :open="Boolean(qr)"
            :title="qr ? `QR · ${qr.sale.name}` : ''"
            help="Codice generato sul server. Scarica SVG, PNG o PDF A4 per stampa."
            show-close
            @update:open="(open) => { if (!open) qr = null; }"
        >
            <template v-if="qr">
                <div class="admin-vue-kit__qr-host">
                    <img
                        class="admin-vue-kit__qr-image"
                        :src="qr.pngUrl"
                        :alt="`QR ${qr.sale.name}`"
                        width="224"
                        height="224"
                    >
                </div>
                <p class="admin-vue-kit__qr-url">{{ qr.url }}</p>
                <div class="admin-vue-kit__qr-actions">
                    <button type="button" class="admin-vue-kit__btn--outline" title="Copia link" @click="copyLink">
                        <i class="fa fa-link" aria-hidden="true" /> Link
                    </button>
                    <a
                        class="admin-vue-kit__btn--outline"
                        :href="`${qr.pngUrl}?format=svg`"
                        :download="`${qr.sale.slug}.svg`"
                        title="Scarica SVG"
                    >
                        <i class="fa fa-download" aria-hidden="true" /> SVG
                    </a>
                    <a
                        class="admin-vue-kit__btn--outline"
                        :href="qr.pngUrl"
                        :download="`${qr.sale.slug}.png`"
                        title="Scarica PNG"
                    >
                        <i class="fa fa-download" aria-hidden="true" /> PNG
                    </a>
                    <a
                        class="admin-vue-kit__btn--outline"
                        :href="`${qr.pngUrl}?format=pdf`"
                        title="Scarica PDF A4 per stampa"
                        :download="`${qr.sale.slug}.pdf`"
                    >
                        <i class="fa fa-download" aria-hidden="true" /> PDF
                    </a>
                </div>
            </template>
        </admin-dialog>
    </admin-island-shell>
</template>

<script>
import { adminAlert, adminFetch } from '@adminpanel-vue-kit/vue-register';
import SaleTypeBadge from '../sale-type-badge/SaleTypeBadge.vue';

export default {
    name: 'PrivateSalesIndex',
    components: { SaleTypeBadge },
    props: {
        payload: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            totalCount: 0,
            editing: null,
            deleting: null,
            forceDeleting: null,
            qr: null,
            productsSale: null,
            productLines: [],
            productQuery: '',
            productResults: [],
            searchTimer: null,
            saving: false,
        };
    },
    computed: {
        tablePermissions() {
            return {
                ...this.payload.permissions,
                canCreate: false,
                canDetail: false,
                canEdit: false,
                canDelete: false,
            };
        },
        tableRoutes() {
            return {
                show: this.payload.routes.show,
            };
        },
    },
    methods: {
        onTableLoaded({ total }) {
            this.totalCount = total;
        },
        reloadTable() {
            this.$refs.table?.reload();
            this.$refs.table?.clearSelection();
        },
        emptySale() {
            return { id: '', name: '', description: '', type: 'party', slug: '', start: '', end: '', status: 'futura' };
        },
        routeFor(name, id) {
            return String(this.payload.routes[name] || '').replace('__ID__', id);
        },
        saleHasProduct(sale) {
            return Boolean(sale?.has_product);
        },
        swalContainerClass() {
            const hasOverlay = Boolean(this.qr || this.editing || this.deleting || this.forceDeleting || this.productsSale);

            return hasOverlay ? 'private-sales-swal-top' : undefined;
        },
        alert(message, icon = 'success') {
            return adminAlert(message, icon, { containerClass: this.swalContainerClass() });
        },
        slugFromName(name) {
            return String(name || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        },
        serverDate(value) {
            const [day = '', time = '00:00'] = String(value).split('T');
            return `${day} ${time.length === 5 ? `${time}:00` : time}`;
        },
        datePart(value) {
            const [day = ''] = String(value).split('T');
            const [year, month, d] = day.split('-');
            if (!year || !month || !d) {
                return '—';
            }
            return `${d}/${month}/${year}`;
        },
        timePart(value) {
            return String(value).split('T')[1] || '';
        },
        openForm(sale) {
            if (sale?.deleted) {
                return;
            }
            this.editing = { ...sale };
        },
        async save() {
            if (!this.editing || this.saving) {
                return;
            }
            if (this.editing.end <= this.editing.start) {
                await this.alert('La fine deve essere successiva all’inizio.', 'error');
                return;
            }
            const body = {
                name: this.editing.name,
                description: this.editing.description || null,
                type: this.editing.type,
                start_time: this.serverDate(this.editing.start),
                end_time: this.serverDate(this.editing.end),
            };
            const creating = !this.editing.id;
            this.saving = true;
            try {
                const data = await adminFetch(
                    creating ? this.routeFor('store') : this.routeFor('update', this.editing.id),
                    { method: creating ? 'POST' : 'PUT', body },
                );
                await this.alert(data.message || (creating ? 'Vendita creata.' : 'Vendita aggiornata.'));
                this.editing = null;
                this.reloadTable();
            } catch (error) {
                await this.alert(error.message, 'error');
            } finally {
                this.saving = false;
            }
        },
        async removeSale() {
            if (!this.deleting || this.saving) {
                return;
            }
            this.saving = true;
            try {
                const data = await adminFetch(this.routeFor('destroy', this.deleting.id), { method: 'DELETE' });
                this.deleting = null;
                await this.alert(data.message || 'Vendita eliminata.');
                this.reloadTable();
            } catch (error) {
                await this.alert(error.message, 'error');
                this.deleting = null;
            } finally {
                this.saving = false;
            }
        },
        async forceDestroySale() {
            if (!this.forceDeleting || this.saving) {
                return;
            }
            this.saving = true;
            try {
                const data = await adminFetch(this.routeFor('forceDestroy', this.forceDeleting.id), { method: 'DELETE' });
                this.forceDeleting = null;
                await this.alert(data.message || 'Vendita eliminata definitivamente.');
                this.reloadTable();
            } catch (error) {
                await this.alert(error.message, 'error');
                this.forceDeleting = null;
            } finally {
                this.saving = false;
            }
        },
        async restoreSale(sale) {
            if (!sale || this.saving) {
                return;
            }
            this.saving = true;
            try {
                const data = await adminFetch(this.routeFor('restore', sale.id), { method: 'POST' });
                await this.alert(data.message || 'Vendita ripristinata.');
                this.reloadTable();
            } catch (error) {
                await this.alert(error.message, 'error');
            } finally {
                this.saving = false;
            }
        },
        async onMassDelete(ids) {
            if (!ids.length || this.saving) {
                return;
            }
            this.saving = true;
            try {
                const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const body = new FormData();
                body.append('_token', token);
                body.append('delete_ids', ids.join(','));
                const response = await fetch(this.payload.routes.massDelete, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': token,
                    },
                    credentials: 'same-origin',
                    body,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || 'Eliminazione non riuscita.');
                }
                await this.alert(data.message || 'Vendite eliminate.');
                this.reloadTable();
            } catch (error) {
                await this.alert(error.message, 'error');
            } finally {
                this.saving = false;
            }
        },
        openProducts(sale) {
            this.productQuery = '';
            this.productResults = [];
            this.productsSale = sale;
            this.productLines = (sale.products || []).map((line) => ({
                ean: line.ean,
                text: line.text || line.ean,
                available_quantity: line.available_quantity === null || line.available_quantity === undefined ? '' : line.available_quantity,
            }));
        },
        scheduleProductSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.searchProducts(), 250);
        },
        async searchProducts() {
            if (this.productLines.length >= 1) {
                this.productResults = [];
                return;
            }
            const needle = this.productQuery.trim();
            if (needle.length < 2) {
                this.productResults = [];
                return;
            }
            try {
                const hits = await adminFetch(`${this.payload.routes.products}?needle=${encodeURIComponent(needle)}&limit=20`);
                this.productResults = Array.isArray(hits) ? hits : [];
            } catch (error) {
                await this.alert(error.message, 'error');
                this.productResults = [];
            }
        },
        addProduct(hit) {
            if (this.productLines.length >= 1 || this.productLines.some((line) => line.ean === hit.ean)) {
                return;
            }
            this.productLines.push({
                ean: hit.ean,
                text: hit.text || hit.ean,
                available_quantity: '',
            });
            this.productQuery = '';
            this.productResults = [];
        },
        removeProduct(ean) {
            this.productLines = this.productLines.filter((line) => line.ean !== ean);
        },
        async saveProducts() {
            if (!this.productsSale || this.saving) {
                return;
            }
            this.saving = true;
            try {
                const data = await adminFetch(this.routeFor('update', this.productsSale.id), {
                    method: 'PUT',
                    body: {
                        products: this.productLines.map((line) => ({
                            ean: line.ean,
                            available_quantity: line.available_quantity === '' || line.available_quantity === null
                                ? null
                                : Number(line.available_quantity),
                        })),
                    },
                });
                await this.alert(data.message || 'Prodotto aggiornato.');
                this.productsSale = null;
                this.reloadTable();
            } catch (error) {
                await this.alert(error.message, 'error');
            } finally {
                this.saving = false;
            }
        },
        async openQr(sale) {
            if (!this.saleHasProduct(sale)) {
                await this.alert('Aggiungi almeno un prodotto prima di generare il QR.', 'error');
                return;
            }
            try {
                const data = await adminFetch(this.routeFor('qr', sale.id));
                this.qr = { sale, url: data.url, pngUrl: this.routeFor('qr', sale.id) };
            } catch (error) {
                await this.alert(error.message, 'error');
            }
        },
        async copyLink() {
            if (!this.qr) {
                return;
            }
            try {
                await navigator.clipboard.writeText(this.qr.url);
                await this.alert('Link copiato.');
            } catch {
                await this.alert('Impossibile copiare il link.', 'error');
            }
        },
    },
};
</script>

<style>
body > .swal2-container.private-sales-swal-top {
    z-index: 2100 !important;
}
</style>
